import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Sword, Coins, Users, X } from 'lucide-react'
import { CardImageModal } from '../components/ui/CardImage'
import { Card, CardType, Faction, Rarity } from '../types'

interface BattleCard extends Card {}

const demoCards: BattleCard[] = [
  { id: '1', cardId: '1', name: 'Aghast el Dragón', imageUrl: '/src/assets/cards/hero/Aghast__El_Drag_n.jpg', cost: 6, strength: 8, type: CardType.UNIT, faction: Faction.DRAGON, rarity: Rarity.LEGENDARY, edition: '1', effect: { description: 'Cuando entra al campo de batalla, destruye una criatura enemiga.' } },
  { id: '2', cardId: '2', name: 'Alabarda', imageUrl: '/src/assets/cards/hero/Alabarda.jpg', cost: 3, strength: 4, type: CardType.UNIT, faction: Faction.HUMAN, rarity: Rarity.COMMON, edition: '1', effect: { description: 'Ninguno' } },
  { id: '3', cardId: '3', name: 'Alto Druida', imageUrl: '/src/assets/cards/hero/Alto_Druida.jpg', cost: 4, strength: 5, type: CardType.UNIT, faction: Faction.ELF, rarity: Rarity.UNCOMMON, edition: '1', effect: { description: '+2 de fuerza a los Elfos aliados.' } },
  { id: '4', cardId: '4', name: 'Arquero Elfo', imageUrl: '/src/assets/cards/hero/Arquero_Elfo.jpg', cost: 2, strength: 3, type: CardType.UNIT, faction: Faction.ELF, rarity: Rarity.COMMON, edition: '1', effect: { description: 'Ataque a distancia' } },
  { id: '5', cardId: '5', name: 'Armada Élfica', imageUrl: '/src/assets/cards/hero/Armada__lfica.jpg', cost: 5, strength: 6, type: CardType.UNIT, faction: Faction.ELF, rarity: Rarity.RARE, edition: '1', effect: { description: 'No puede ser bloqueada.' } },
  { id: '6', cardId: '6', name: 'Amada de los Bosques', imageUrl: '/src/assets/cards/hero/Amada_de_los_Bosques.jpg', cost: 4, strength: 4, type: CardType.UNIT, faction: Faction.ELF, rarity: Rarity.MYTHIC, edition: '1', effect: { description: 'Evoca 2 Hijos de Daana.' } },
]

export default function BattleArenaPage() {
  const [turn] = useState(1)
  const [playerHealth] = useState(20)
  const [opponentHealth] = useState(20)
  const [goldPaid, setGoldPaid] = useState(3)
  const [goldReserve, setGoldReserve] = useState(2)
  const [selectedCard, setSelectedCard] = useState<BattleCard | null>(null)
  const [attackLine] = useState<BattleCard[]>([demoCards[0], demoCards[1]])
  const [defenseLine] = useState<BattleCard[]>([demoCards[2]])
  const [supportLine] = useState<BattleCard[]>([demoCards[3], demoCards[4]])
  const [hand] = useState<BattleCard[]>([demoCards[0], demoCards[1], demoCards[2], demoCards[3]])
  const [cemetery] = useState<{ card: BattleCard; quantity: number }[]>([
    { card: demoCards[0], quantity: 2 },
    { card: demoCards[1], quantity: 1 },
    { card: demoCards[2], quantity: 3 },
  ])
  const [selectedCemeteryCard, setSelectedCemeteryCard] = useState<{ card: BattleCard; quantity: number } | null>(null)
  const [goldCards] = useState<{ card: BattleCard; quantity: number }[]>([
    { card: demoCards[0], quantity: 2 },
    { card: demoCards[1], quantity: 1 },
  ])
  const [showGoldModal, setShowGoldModal] = useState(false)

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <header className="bg-slate-800/50 border-b border-slate-700 p-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm">
            <ArrowLeft size={16} />
            Volver
          </Link>
          <h1 className="text-lg font-bold text-white flex items-center gap-2">
            <Sword className="text-amber-500" size={20} />
            ARENA DE BATALLA
          </h1>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2 text-red-400">
              <Users size={16} />
              <span className="font-medium">Oponente: {opponentHealth} HP</span>
            </div>
            <div className="flex items-center gap-2 text-green-400">
              <span className="font-medium">Tú: {playerHealth} HP</span>
            </div>
          </div>
        </div>
      </header>

      <main className="p-3">
        <div className="max-w-6xl mx-auto space-y-3">
          <div className="text-center">
            <h2 className="text-xl font-bold text-white tracking-wider">ARENA DE JUEGO</h2>
            <p className="text-slate-400 text-sm">Turno {turn} • Fase de Combate</p>
          </div>

          <div className="flex items-center justify-center gap-6">
            <div className="w-24 h-32 rounded-lg border-2 border-red-500/50 bg-slate-900/50 flex items-center justify-center relative overflow-hidden">
              <img src={demoCards[0].imageUrl} alt="Oponente" className="absolute inset-0 w-full h-full object-cover opacity-50" />
              <span className="relative z-10 text-red-400 font-bold text-lg">?</span>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{playerHealth} - {opponentHealth}</div>
              <div className="text-slate-400 text-xs">Puntos de Vida</div>
            </div>
            <div className="w-24 h-32 rounded-lg border-2 border-amber-500/50 bg-amber-900/20 flex items-center justify-center">
              <div className="text-center">
                <Coins className="text-amber-400 mx-auto mb-1" size={24} />
                <div className="text-2xl font-bold text-amber-400">{goldPaid}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-2 grid grid-rows-3 gap-2">
              <div className="h-[5.5rem]"></div>
              <div className="text-center flex items-center justify-center">
                <div 
                  className="w-28 h-40 rounded-lg border-2 border-slate-600 bg-slate-900/50 flex items-center justify-center overflow-hidden cursor-pointer hover:ring-2 hover:ring-slate-400 transition-all"
                  onClick={() => setSelectedCemeteryCard({ card: demoCards[0], quantity: 2 })}
                >
                  <div className="grid grid-cols-2 gap-0.5 p-1 w-full h-full">
                    {cemetery.map((item, i) => (
                      <div key={i} className="relative">
                        <img src={item.card.imageUrl} alt={item.card.name} className="w-full h-full object-cover rounded" />
                        {item.quantity > 1 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center font-bold">
                            {item.quantity}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="text-center flex items-center justify-center">
                <div className="w-28 h-40 rounded-lg border-2 border-purple-600/50 bg-purple-900/20 flex items-center justify-center overflow-hidden">
                  <img src={demoCards[5].imageUrl} alt="Destierro" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>

            <div className="col-span-2 grid grid-rows-3 gap-2">
              <div className="text-center flex items-center justify-center">
                <div 
                  className="w-28 h-40 rounded-lg border-2 border-dashed border-amber-500/50 bg-amber-900/20 flex items-center justify-center cursor-pointer hover:bg-amber-800/30 hover:border-amber-400 transition-all"
                  onClick={() => {
                    if (goldReserve > 0) {
                      setGoldPaid(prev => prev + 1)
                      setGoldReserve(prev => prev - 1)
                    }
                  }}
                >
                  <div className="text-center">
                    <Coins className="text-amber-400 mx-auto mb-1" size={32} />
                    <div className="text-2xl font-bold text-amber-400">{goldPaid}</div>
                  </div>
                </div>
              </div>
              <div className="text-center flex items-center justify-center">
                <div className="w-28 h-40 rounded-lg border-2 border-amber-600/50 bg-amber-900/20 flex items-center justify-center overflow-hidden">
                  <img src="/src/assets/cards/back_myl.png" alt="Mazo" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="text-center flex items-center justify-center">
                <div 
                  className="w-28 h-40 rounded-lg border-2 border-dashed border-yellow-600/50 bg-yellow-900/20 flex items-center justify-center overflow-hidden relative cursor-pointer hover:bg-yellow-800/30 hover:border-yellow-400 transition-all"
                  onClick={() => setShowGoldModal(true)}
                >
                  <img src="/src/assets/cards/hero/Anillo_de_Fe.jpg" alt="Oro Inicial" className="w-full h-full object-cover" />
                  <div className="absolute bottom-1 right-1 w-7 h-7 bg-yellow-500 rounded-full flex items-center justify-center font-bold text-slate-900 shadow">
                    {goldReserve}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-8 space-y-2">
              <div className="relative group">
                <div className="absolute inset-0 border-2 border-dashed border-slate-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                <div className="flex items-center gap-2 overflow-x-auto p-2">
                  {attackLine.map((card) => (
                    <BattleCardComponent key={card.id} card={card} onClick={setSelectedCard} />
                  ))}
                </div>
              </div>
              <div className="relative group">
                <div className="absolute inset-0 border-2 border-dashed border-slate-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                <div className="flex items-center gap-2 overflow-x-auto p-2">
                  {defenseLine.map((card) => (
                    <BattleCardComponent key={card.id} card={card} onClick={setSelectedCard} />
                  ))}
                </div>
              </div>
              <div className="relative group">
                <div className="absolute inset-0 border-2 border-dashed border-slate-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                <div className="flex items-center gap-2 overflow-x-auto p-2">
                  {supportLine.map((card) => (
                    <BattleCardComponent key={card.id} card={card} onClick={setSelectedCard} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-3">
            <p className="text-slate-400 text-xs mb-2 text-center">MANO (MAX 8)</p>
            <div className="flex justify-center gap-2 overflow-x-auto py-2 px-4">
              {hand.map((card) => (
                <BattleCardComponent key={card.id} card={card} onClick={setSelectedCard} />
              ))}
              {Array.from({ length: Math.max(0, 8 - hand.length) }).map((_, i) => (
                <div key={`empty-${i}`} className="w-20 h-28 rounded-lg border-2 border-dashed border-slate-600 bg-slate-800/30 flex-shrink-0" />
              ))}
            </div>
          </div>
        </div>
      </main>

      {selectedCard && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedCard(null)}>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelectedCard(null)} className="absolute -top-10 right-0 text-white hover:text-slate-300">
              <X size={32} />
            </button>
            <CardImageModal card={selectedCard} />
          </div>
        </div>
      )}

      {selectedCemeteryCard && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedCemeteryCard(null)}>
          <div className="bg-slate-800 rounded-xl p-6 max-w-4xl w-full border border-slate-600 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelectedCemeteryCard(null)} className="absolute top-3 right-3 text-slate-400 hover:text-white">
              <X size={24} />
            </button>
            <h3 className="text-xl font-bold text-white text-center mb-4">CEMENTERIO</h3>
            <div className="grid grid-cols-4 gap-4 max-h-[70vh] overflow-y-auto">
              {cemetery.map((item, i) => (
                <div key={i} className="text-center">
                  <div 
                    className="w-full aspect-[2/3] rounded-lg overflow-hidden shadow-lg border-2 border-slate-500 cursor-pointer hover:ring-2 hover:ring-slate-300 transition-all"
                    onClick={() => {
                      setSelectedCemeteryCard(null)
                      setSelectedCard(item.card)
                    }}
                  >
                    <img src={item.card.imageUrl} alt={item.card.name} className="w-full h-full object-cover" />
                  </div>
                  <span className="text-red-400 font-bold text-lg">x{item.quantity}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showGoldModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowGoldModal(false)}>
          <div className="bg-slate-800 rounded-xl p-6 max-w-4xl w-full border border-slate-600 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowGoldModal(false)} className="absolute top-3 right-3 text-slate-400 hover:text-white">
              <X size={24} />
            </button>
            <h3 className="text-xl font-bold text-white text-center mb-4">RESERVA DE ORO</h3>
            <div className="flex justify-center gap-4 mb-6">
              <button 
                onClick={() => {
                  if (goldReserve > 0) {
                    setGoldPaid(prev => prev + 1)
                    setGoldReserve(prev => prev - 1)
                  }
                }}
                disabled={goldReserve === 0}
                className="px-6 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 disabled:text-slate-400 text-white font-bold rounded-lg transition-colors flex items-center gap-2"
              >
                <Coins size={20} />
                PAGAR ORO
              </button>
              <button 
                onClick={() => {
                  const totalGold = goldReserve + goldPaid
                  setGoldPaid(0)
                  setGoldReserve(totalGold)
                }}
                disabled={goldPaid === 0}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-600 disabled:text-slate-400 text-white font-bold rounded-lg transition-colors flex items-center gap-2"
              >
                <Coins size={20} />
                AGRUPAR ORO
              </button>
            </div>
            <div className="grid grid-cols-4 gap-4 max-h-[60vh] overflow-y-auto">
              {goldCards.map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-full aspect-[2/3] rounded-lg overflow-hidden shadow-lg border-2 border-yellow-500/50">
                    <img src={item.card.imageUrl} alt={item.card.name} className="w-full h-full object-cover" />
                  </div>
                  <span className="text-yellow-400 font-bold text-lg">x{item.quantity}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function BattleCardComponent({ card, onClick }: { card: BattleCard; onClick: (card: BattleCard) => void }) {
  return (
    <div 
      className="relative w-28 h-40 rounded-lg overflow-hidden shadow-lg border-2 border-amber-500/50 flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-amber-400 transition-all"
      onClick={() => onClick(card)}
    >
      <img src={card.imageUrl} alt={card.name} className="w-full h-full object-cover" />
    </div>
  )
}


